import { useEffect, useRef, useState } from 'react';

import { Apollo } from 'src/gql/generated';

import type { AlineMarkdownEditorRef } from '../ChatPanel/AlineMarkdownEditor';
import {
  deserializeMarkdownWithMentions,
  focusEditor,
  getIsEditorWithMarkdownApi,
} from '../ChatPanel/ChatEditor';
import { useTypewriter } from '../ChatPanelMarkdown/hooks/useTypewriter';

interface UseStreamImprovePromptArgs {
  editorRef: React.RefObject<AlineMarkdownEditorRef | null>;
  improveRequestId: string | null;
  onComplete?: () => void;
  onStreamStart?: () => void;
}

interface StreamState {
  ended: boolean;
  prevDisplayedText: string;
  started: boolean;
  text: string;
}

const INITIAL_STREAM_STATE: StreamState = {
  ended: false,
  prevDisplayedText: '',
  started: false,
  text: '',
};

/**
 * Hook for improve prompt feature.
 * Handles GraphQL subscription and uses useTypewriter to stream text character-by-character.
 */
export function useStreamImprovePrompt({
  editorRef,
  improveRequestId,
  onComplete,
  onStreamStart,
}: UseStreamImprovePromptArgs): void {
  // Stable reference to callbacks to avoid infinite loops in effects.
  const callbacks = useRef({ onComplete, onStreamStart });
  const [stream, setStream] = useState<StreamState>(INITIAL_STREAM_STATE);

  useEffect(() => {
    callbacks.current = { onComplete, onStreamStart };
  }, [onComplete, onStreamStart]);

  Apollo.useOnImprovedPromptSubscription({
    onData: ({ data }) => {
      const event = data.data?.onImprovedPrompt;
      if (!event) return;

      const { improvedText, position, requestId } = event;
      if (requestId !== improveRequestId) return;

      // Call onStreamStart when first chunk arrives...
      if (!stream.started && improvedText) callbacks.current.onStreamStart?.();

      setStream(prev => {
        return {
          ...prev,
          ended: position === Apollo.CompletionPosition.End,
          started: true,
          text: improvedText,
        };
      });
    },
    skip: !improveRequestId,
    variables: { requestId: improveRequestId ?? '' },
  });

  // Use the existing typewriter hook to buffer the streamed text for smooth animation.
  const displayedText = useTypewriter({
    isMessageStreaming: !stream.ended && !!improveRequestId,
    message: { id: improveRequestId ?? '', text: stream.text },
  });

  useEffect(() => {
    const editor = editorRef.current?.getEditor();
    if (!displayedText || !improveRequestId || !editor || !getIsEditorWithMarkdownApi(editor))
      return;

    editor.tf.setValue(deserializeMarkdownWithMentions(editor, displayedText));
    // Defer focus to next frame so DOM has time to update after setValue.
    requestAnimationFrame(() => focusEditor(editor, 'end'));

    if (stream.ended && displayedText === stream.text) callbacks.current.onComplete?.();
  }, [displayedText, editorRef, improveRequestId, stream]);

  useEffect(() => {
    setStream(INITIAL_STREAM_STATE);
  }, [improveRequestId]);
}
